import React, { useState } from 'react';
import { X, Send, User, Mail, MessageSquare, Phone, Building2 } from 'lucide-react';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      setError('Please fill in all required fields');
      setSubmitting(false);
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

      const response = await fetch(`${API_URL}/email/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
          source: 'website_contact_form'
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          onClose();
          setSubmitted(false);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            company: '',
            message: ''
          });
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.detail || 'An error occurred');
      }
    } catch (err) {
      setError('Connection error. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contact-modal-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header with logo */}
        <div className="contact-modal-header" style={{ height: '90px' }}>
            <div className="contact-modal-logo">
                <img 
                src="/uploads/logos/logo_without_background.png" 
                alt="aeroTrick Logo" 
                className="contact-logo-img"
                style={{ height: '120px', flexShrink: 0, position: 'relative', top: '10px' }}
                />
                <div>
                <div className="contact-logo-name">aero<span>Trick</span></div>
                <span className="contact-logo-sub">FBO Management Solutions</span>
                </div>
            </div>
            <button className="contact-modal-close" onClick={onClose}>
                <X size={24} />
            </button>
            </div>

        {submitted ? (
          <div className="contact-success">
            <div className="contact-success-icon">✓</div>
            <h3>Message Sent Successfully!</h3>
            <p>Thank you for contacting us. Our team will get back to you within 24 hours.</p>
            <button className="contact-btn-close" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className="contact-modal-body">
              <h2 className="contact-title">Contact Us</h2>
              <p className="contact-subtitle">Have a question? We'd love to hear from you.</p>

              <form onSubmit={handleSubmit}>
                {/* Contact Information */}
                <div className="contact-section">
                  <h3 className="contact-section-title">
                    <User size={18} />
                    Your Information
                  </h3>
                  <div className="contact-row">
                    <div className="contact-field">
                      <label>First Name *</label>
                      <input 
                        type="text" 
                        name="firstName" 
                        value={formData.firstName} 
                        onChange={handleChange} 
                        placeholder="John"
                        required 
                      />
                    </div>
                    <div className="contact-field">
                      <label>Last Name *</label>
                      <input 
                        type="text" 
                        name="lastName" 
                        value={formData.lastName} 
                        onChange={handleChange} 
                        placeholder="Doe"
                        required 
                      />
                    </div>
                  </div>
                  <div className="contact-row">
                    <div className="contact-field">
                      <label>Email *</label>
                      <input 
                        type="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleChange} 
                        placeholder="john@example.com"
                        required 
                      />
                    </div>
                    <div className="contact-field">
                      <label>Phone</label>
                      <input 
                        type="tel" 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleChange} 
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                  <div className="contact-field">
                    <label>Company / FBO Name</label>
                    <input 
                      type="text" 
                      name="company" 
                      value={formData.company} 
                      onChange={handleChange} 
                      placeholder="Your FBO or company name"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="contact-section">
                  <h3 className="contact-section-title">
                    <MessageSquare size={18} />
                    Your Message
                  </h3>
                  <div className="contact-field">
                    <label>Message *</label>
                    <textarea 
                      name="message" 
                      rows="5" 
                      value={formData.message} 
                      onChange={handleChange} 
                      placeholder="Tell us about your question, project, or how we can help..."
                      required
                    ></textarea>
                  </div>
                </div>

                {error && <div className="contact-error">{error}</div>}

                <button type="submit" className="contact-submit-btn" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send Message'}
                  <Send size={16} />
                </button>
              </form>
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        .contact-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow-y: auto;
          padding: 20px;
        }
        
        .contact-modal {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 24px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          border: 1px solid rgba(59, 130, 246, 0.3);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }
        
        .contact-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid rgba(148, 163, 184, 0.1);
          background: rgba(8, 13, 26, 0.8);
          position: sticky;
          top: 0;
          z-index: 10;
          overflow: visible;
          height: 70px;
        }
        
        .contact-modal-logo {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .contact-logo-img {
          height: 48px;
          width: auto;
        }
        
        .contact-logo-name {
          font-size: 20px;
          font-weight: 700;
          color: white;
        }
        
        .contact-logo-name span {
          color: #3b82f6;
        }
        
        .contact-logo-sub {
          font-size: 10px;
          color: #94a3b8;
          letter-spacing: 1px;
          text-transform: uppercase;
        }
        
        .contact-modal-close {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #94a3b8;
          transition: all 0.2s;
        }
        
        .contact-modal-close:hover {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }
        
        .contact-modal-body {
          padding: 32px;
        }
        
        .contact-title {
          font-size: 28px;
          font-weight: 700;
          color: white;
          margin-bottom: 8px;
        }
        
        .contact-subtitle {
          color: #94a3b8;
          margin-bottom: 32px;
          font-size: 14px;
        }
        
        .contact-section {
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid rgba(148, 163, 184, 0.1);
        }
        
        .contact-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 16px;
          font-weight: 600;
          color: #60a5fa;
          margin-bottom: 20px;
        }
        
        .contact-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }
        
        .contact-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .contact-field label {
          font-size: 12px;
          font-weight: 500;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .contact-field input,
        .contact-field select,
        .contact-field textarea {
          background: rgba(8, 13, 26, 0.8);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          padding: 12px 14px;
          color: white;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .contact-field input:focus,
        .contact-field select:focus,
        .contact-field textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .contact-field input::placeholder,
        .contact-field textarea::placeholder {
          color: #475569;
        }
        
        .contact-submit-btn {
          width: 100%;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          border: none;
          border-radius: 12px;
          padding: 14px;
          color: white;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 16px;
        }
        
        .contact-submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
        }
        
        .contact-submit-btn:disabled {
          opacity: 0.6;
          transform: none;
        }
        
        .contact-error {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 10px;
          padding: 12px;
          color: #f87171;
          font-size: 13px;
          margin-bottom: 16px;
        }
        
        .contact-success {
          text-align: center;
          padding: 48px 32px;
        }
        
        .contact-success-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          color: white;
          margin: 0 auto 20px;
        }
        
        .contact-success h3 {
          font-size: 24px;
          color: white;
          margin-bottom: 12px;
        }
        
        .contact-success p {
          color: #94a3b8;
          margin-bottom: 24px;
        }
        
        .contact-btn-close {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          padding: 10px 24px;
          color: white;
          cursor: pointer;
        }
        
        @media (max-width: 640px) {
          .contact-row {
            grid-template-columns: 1fr;
          }
          .contact-modal-body {
            padding: 20px;
          }
          .contact-title {
            font-size: 24px;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactModal;